import type { User } from '@/types/user';

type NameSource = Partial<Pick<User, 'display_name' | 'handler' | 'first_name' | 'last_name'>>;

const FLOOR = 'Member';

/**
 * Resolve a user's display label using a legacy-preserving cascade.
 *
 * Order: display_name → handler → first_name + last_name → "Member".
 * display_name first ensures existing users (who all have one) see no UI
 * change after the backend drops the `required` rule on /profile/update-profile.
 */
export function getUserDisplay(user: NameSource | null | undefined): string {
  if (!user) return FLOOR;

  const displayName = user.display_name?.trim();
  if (displayName) return displayName;

  const handler = user.handler?.trim();
  if (handler) return handler;

  const fullName = [user.first_name, user.last_name]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  if (fullName) return fullName;

  return FLOOR;
}

/**
 * First character of the resolved display label, uppercased.
 * Safe for Avatar / image fallback initials — never empty.
 */
export function getUserInitial(user: NameSource | null | undefined): string {
  return getUserDisplay(user).charAt(0).toUpperCase();
}
